import { Box, useDisclosure } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { accAdd, accGte } from "@/utils/formatBalance";
import Pay from "./Pay";
import TipAmountModal from "./TipAmountModal";
import { useNavigate, useParams } from "react-router-dom";
import PayLoading from "./PayLoading";
import { ethInfo, usdcInfo } from "@/constants/tokens";
import SelectAsset from "./SelectAsset";
import { TokenInfo, useUserInfo } from "@/context/UserInfo";
import { useTransfer } from "@/hooks/useTransfer";
import { getOrder, Order, Status } from "@/api/order";
import LoadingPage from "@/components/LoadingPage";
import { useNetworkFee } from "@/hooks/useNetworkFee";

const User = () => {
  const navigate = useNavigate();
  const {
    isOpen: isTipOpen,
    onOpen: onTipOpen,
    onClose: onTipClose,
  } = useDisclosure();

  const {
    isOpen: isSelectAssetOpen,
    onOpen: onSelectAssetOpen,
    onClose: onSelectAssetClose,
  } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [tipActiveIndex, setTipActiveIndex] = useState(0);
  const [customTip, setCustomTip] = useState("0");
  const [currentTip, setCurrentTip] = useState("0");
  const { orderId } = useParams();
  const [orderDetail, setOrderDetail] = useState<Order | null>(null);

  const { tokenInfo, balanceLoading } = useUserInfo();
  // 添加当前选择的代币状态
  const [selectedToken, setSelectedToken] = useState<TokenInfo | null>(null);

  const { networkFeeAmount, networkFeeValue } = useNetworkFee({
    token: selectedToken,
  });

  const {
    isNormalPay,
    feeAmount,
    tipAmount,
    recipientAmount,
    taxAmount,
    orderAmount,
    balanceDue,
    recipient,
  } = useMemo(() => {
    if (!orderDetail)
      return {
        isNormalPay: false,
        feeAmount: "0",
        tipAmount: "0",
        recipientAmount: "0",
        taxAmount: "0",
        orderAmount: "0",
        balanceDue: "0",
        recipient: "",
        orderId: "",
      };

    const isNormalPay = orderDetail?.orderType === "normal";
    const tipAmount = currentTip;
    const feeAmount = orderDetail.feeAmount;
    const recipientAmount = orderDetail.recipientAmount;
    const taxAmount = orderDetail.taxAmount;
    const orderAmount = orderDetail.orderAmount;
    const balanceDue = accAdd(
      accAdd(accAdd(orderAmount, taxAmount), tipAmount),
      networkFeeValue
    );
    return {
      isNormalPay,
      feeAmount,
      tipAmount,
      recipientAmount,
      taxAmount,
      orderAmount,
      balanceDue,
      recipient: orderDetail.recipient?.address || "",
    };
  }, [orderDetail, currentTip, networkFeeValue]);

  const {
    transfer,
    status,
    swapBeforeError,
    swapAfterError,
    networkFee,
    resetError,
  } = useTransfer({
    token: selectedToken,
    orderId: orderId,
    tipAmount,
    feeAmount,
    recipientAmount,
    taxAmount,
    orderAmount,
    recipient: recipient || "",
  });

  useEffect(() => {
    if (!tokenInfo || !orderDetail) return;

    // 判断usdc的余额是否大于订单金额
    const usdcBalance = tokenInfo[usdcInfo.address]?.balance || "0"; // 直接通过键访问USDC余额
    const orderAmount = orderDetail.recipientAmount;

    if (accGte(usdcBalance, orderAmount)) {
      // 如果usdc余额大于订单金额，选择usdc
      setSelectedToken(tokenInfo[usdcInfo.address]);
    } else {
      // 从列表中选择大于订单金额的代币
      const availableToken = Object.values(tokenInfo).find((token) =>
        accGte(token.value, orderAmount)
      );
      setSelectedToken(
        availableToken || {
          ...ethInfo,
          balance: tokenInfo[ethInfo.address]?.balance || "0",
          value: tokenInfo[ethInfo.address]?.value || "0",
        }
      );
    }
  }, [orderDetail, tokenInfo]);

  const handleTipActiveIndexChange = (index: number, tipAmount: string) => {
    setCurrentTip(tipAmount);
    setTipActiveIndex(index);
  };

  const handleSelectAssetOpen = () => {
    onSelectAssetOpen();
  };

  const handleSelectToken = (token: TokenInfo) => {
    setSelectedToken(token);
    onSelectAssetClose();
  };

  const getOrderDetail = async () => {
    try {
      setLoading(true);
      const order = await getOrder(orderId!);
      if (order.status === Status.Completed) {
        navigate(`/receipt/${orderId}`);
        return;
      }
      setOrderDetail({
        ...order,
      });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orderId) return;

    getOrderDetail();
  }, [orderId]);

  if (loading || balanceLoading) {
    return <LoadingPage text="Fetching transaction info" />;
  }

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
      }}
      onClick={() => {
        resetError();
      }}
    >
      <Pay
        isNormalPay={isNormalPay}
        swapBeforeError={swapBeforeError}
        swapAfterError={swapAfterError}
        selectedToken={selectedToken}
        orderDetail={orderDetail}
        currentTip={currentTip}
        customTip={customTip}
        onConfirm={transfer}
        onTipOpen={onTipOpen}
        onSelectAssetOpen={handleSelectAssetOpen}
        onTipActiveIndexChange={handleTipActiveIndexChange}
        tipActiveIndex={tipActiveIndex}
        balanceDue={balanceDue}
        networkFeeAmount={networkFeeAmount}
        networkFeeValue={networkFeeValue}
      ></Pay>

      <TipAmountModal
        isOpen={isTipOpen}
        onClose={onTipClose}
        onValueChange={(value) => {
          setCustomTip(value);
          setCurrentTip(value);
          onTipClose();
        }}
      ></TipAmountModal>
      <PayLoading payStatus={status}></PayLoading>
      <SelectAsset
        balanceDue={balanceDue}
        selectedToken={selectedToken}
        onSelectToken={handleSelectToken}
        isOpen={isSelectAssetOpen}
        onClose={onSelectAssetClose}
      ></SelectAsset>
    </Box>
  );
};

export default User;
